import { useEffect, useState } from "react";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { SecurityClearanceDetails } from "@services/security-clearance/types";
import { getMySecurityClearance, getSecurityClearanceById } from "@services/securityClearanceApi";
import { useQuery } from "@tanstack/react-query";
import { isIndividualApplicantUser } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";

interface SecurityClearanceValidation {
  isValid: boolean;
  isLoading: boolean;
  validationMessage: string;
  securityClearance?: SecurityClearanceDetails;
}

type CurrentSecurityClearance = Partial<SecurityClearanceDetails> & {
  securityClearanceId?: string;
  securityClearanceRequestId?: string;
  securityClearanceStatus?: string;
};

/**
 * Hook to validate if the current individual has a valid security clearance
 * Valid conditions:
 * - Security clearance exists
 * - Status is APPROVED
 * - Not expired
 * - Visa not expired (if applicable)
 * - Passport/Emirates ID not expired
 */
export const useSecurityClearanceValidation = (): SecurityClearanceValidation => {
  const { t } = useTranslation();
  const { user, bootstrapSecurityClearanceId } = useAuthStore();
  const userId = user?.id ?? null;
  const isIndividualUser = isIndividualApplicantUser(user?.roles);
  const validationId = isIndividualUser ? userId : bootstrapSecurityClearanceId;
  const [validationResult, setValidationResult] = useState<SecurityClearanceValidation>({
    isValid: false,
    isLoading: true,
    validationMessage: t("gatepass.notifications.sc-validation-error"),
  });

  const {
    data: securityClearance,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["securityClearanceValidation", validationId, isIndividualUser ? "me" : "id"],
    queryFn: async () => {
      if (isIndividualUser) {
        const current = (await getMySecurityClearance({
          service: "bff-gatepass",
        })) as CurrentSecurityClearance;
        const currentId = String(
          current.securityClearanceId ?? current.securityClearanceRequestId ?? current.id ?? "",
        ).trim();
        const currentStatus = String(
          current.status ?? current.securityClearanceStatus ?? "",
        ).toUpperCase();

        if (currentId && (currentStatus === "APPROVED" || !currentStatus)) {
          return getSecurityClearanceById({ id: currentId, service: "bff-gatepass" });
        }

        return current as SecurityClearanceDetails;
      }

      return bootstrapSecurityClearanceId
        ? getSecurityClearanceById({
            id: bootstrapSecurityClearanceId,
            service: "bff-gatepass",
          })
        : null;
    },
    enabled: !!validationId,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const parseDateOnlyAsLocal = (value: string): Date => {
    const [year, month, day] = value.slice(0, 10).split("-").map(Number);

    if ([year, month, day].some(Number.isNaN)) {
      return new Date(value);
    }

    return new Date(year, month - 1, day);
  };

  useEffect(() => {
    if (isLoading) {
      setValidationResult({
        isValid: false,
        isLoading: true,
        validationMessage: t("gatepass.notifications.sc-validation-error"),
      });
      return;
    }

    if (error || !securityClearance) {
      setValidationResult({
        isValid: false,
        isLoading: false,
        validationMessage: t("gatepass.notifications.no-security-clearance-id"),
      });
      return;
    }

    const status = String(
      (securityClearance as CurrentSecurityClearance).status ??
        (securityClearance as CurrentSecurityClearance).securityClearanceStatus ??
        "",
    ).toUpperCase();

    // Check if security clearance is approved
    if (status !== "APPROVED") {
      setValidationResult({
        isValid: false,
        isLoading: false,
        validationMessage: t("gatepass.notifications.sc-not-approved"),
        securityClearance,
      });
      return;
    }

    // Check if security clearance is expired
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    let expiryDate: Date;

    if (securityClearance.duration != null) {
      expiryDate = parseDateOnlyAsLocal(securityClearance.duration);
    } else {
      const approvalOrApplicationDate =
        securityClearance.approvalDate || securityClearance.applicationDate;

      if (!approvalOrApplicationDate) {
        setValidationResult({
          isValid: false,
          isLoading: false,
          validationMessage: t("gatepass.notifications.sc-no-date"),
          securityClearance,
        });
        return;
      }

      const approvalDate = parseDateOnlyAsLocal(approvalOrApplicationDate);
      const validityDays = securityClearance.validityPeriod || 0;
      expiryDate = new Date(approvalDate.getTime() + validityDays * 24 * 60 * 60 * 1000);
    }

    if (expiryDate < todayStart) {
      setValidationResult({
        isValid: false,
        isLoading: false,
        validationMessage: t("gatepass.notifications.sc-expired"),
        securityClearance,
      });
      return;
    }

    // Check passport/Emirates ID expiry
    const passportExpiryDate = securityClearance.applicant?.passportExpiryDate
      ? parseDateOnlyAsLocal(securityClearance.applicant.passportExpiryDate)
      : null;

    if (passportExpiryDate && passportExpiryDate < todayStart) {
      setValidationResult({
        isValid: false,
        isLoading: false,
        validationMessage: t("gatepass.notifications.passport-id-expired"),
        securityClearance,
      });
      return;
    }

    // Check visa expiry (if visa information is available)
    const visaExpiryDate = securityClearance.residenceStatus?.expiryDate
      ? parseDateOnlyAsLocal(securityClearance.residenceStatus.expiryDate)
      : null;

    if (visaExpiryDate && visaExpiryDate < todayStart) {
      setValidationResult({
        isValid: false,
        isLoading: false,
        validationMessage: t("gatepass.notifications.visa-expired"),
        securityClearance,
      });
      return;
    }

    // All validations passed
    setValidationResult({
      isValid: true,
      isLoading: false,
      validationMessage: t("gatepass.notifications.sc-valid"),
      securityClearance,
    });
  }, [securityClearance, isLoading, error, t]);

  return validationResult;
};
